
import { buildAssetUri } from '@/utils/mixins';
import { ceil } from 'lodash';
import StarIcon from '~/components/ui/icons/StarIcon.vue';
import PortfolioPrivacyTag from '../../atom/PortfolioPrivacyTag.vue';
import { PortfolioInteractionType } from '@/utils/behavior/applicationEvents/gtm-data-types';
import { eventManager } from '@/utils/behavior/applicationEvents/event-manager';
import PortfolioRankTag from '../../atom/PortfolioRankTag.vue';
export default {
  name: 'UIPortfolioCard',
  components: {
    StarIcon,
    PortfolioPrivacyTag,
    PortfolioRankTag,
  },
  mixins: [buildAssetUri],
  props: {
    /**
     * Rank information
     * If 0, no medals displayed
     */
    rank: {
      type: Number,
      default: 0,
    },
    /**
     * Overall return of the portfolio (%)
     */
    overallReturn: {
      type: Number,
      required: true,
    },
    /**
     * Today's return (%)
     */
    todayReturn: {
      type: Number,
      required: true,
    },
    /**
     * Name of the portfolio
     */
    name: {
      type: String,
      required: true,
    },
    /**
     * Url of the profile picture
     * of the portfolio's owner
     */
    profileImg: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Display name of the portfolio owner
     * used for default profile picture
     */
    ownerDisplayName: {
      type: String,
      required: true,
    },
    /**
     * ID of the profile owning the portfolio
     */
    ownerId: {
      type: String,
      required: true,
    },
    /**
     * Wether the user likes the portfolio or not
     */
    isLiked: {
      type: Boolean,
      default: false,
    },
    /**
     * Wether the user likes the portfolio or not
     */
    isFavorited: {
      type: Boolean,
      default: false,
    },
    /**
     * The like count of the portfolio
     */
    likeCount: {
      type: Number,
      required: true,
    },
    /**
     * The favorite count of the portfolio
     */
    favoriteCount: {
      type: Number,
      required: true,
    },
    /**
     * Wether we should display Today's performance
     */
    showToday: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Wether we should display Today's performance
     */
    commentCount: {
      type: Number,
      required: true,
    },
    /**
     * Wether we should display Overall performance
     */
    showOverall: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Wether we should display social interactions
     */
    showSocialInteractions: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Current user ID
     */
    profileId: {
      type: String,
      required: false,
      default: '',
    },
    showRankingBorders: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Wether we should display the compare button
     */
    showCompare: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOnboarding: {
      type: Boolean,
      required: false,
      default: false,
    },
    isPublic: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Allows us to ignore mobile decimal restriction when displaying change percent
     */
    ignoreMobileDecimalRestriction: {
      type: Boolean,
      required: false,
      default: false,
    },
    isRepost: {
      type: Boolean,
      required: false,
      default: false,
    },
    isLeaguePortfolioCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    showHyphen: {
      type: Boolean,
      default: false,
    },
    isInPrivateLeague: {
      type: Boolean,
      required: false,
      default: false,
    },
    leagueAvatarUri: {
      type: String,
      required: false,
      default: null,
    },
    inUnrankedState: {
      type: Boolean,
      default: false,
    },
    displayType: {
      type: String,
      default: 'card',
      validator: (v) => ['card', 'round'].includes(v),
    },
    showChangePercentTag: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onPortfolioClick', 'onLike', 'onFavoritePortfolio', 'onCard'],
  data() {
    return {
      defaultUri: require('@/assets/img/unknow-user.jpg'),
    };
  },
  computed: {
    favoriteIconFill() {
      return this.isFavorited ? 'primary-6' : '';
    },
    favoriteIconStroke() {
      return this.isFavorited ? 'primary-6' : 'neutral-10';
    },
    favoriteTooltip() {
      return this.isFavorited
        ? this.$t('portfolio_remove_favorite')
        : this.$t('portfolio_add_favorite');
    },
    isMyPortfolio() {
      return this.ownerId === this.profileId;
    },
    displayTodayReturn() {
      return this.todayReturn ? ceil(this.todayReturn, 2) : 0;
    },
    todayReturnTooltip() {
      return (
        (this.todayReturn
          ? parseFloat(ceil(this.todayReturn, 4)).toFixed(4)
          : '0') + '%'
      );
    },
    showBorder() {
      return this.isRepost;
    },
    rankClasses() {
      switch (this.rank) {
        case 1:
          return 'bg-league-rank-bg-first text-league-rank-text-first border-2 border-league-rank-border-first';
        case 2:
          return 'bg-league-rank-bg-second text-league-rank-text-second border-2 border-league-rank-border-second';
        case 3:
          return 'bg-league-rank-bg-third text-league-rank-text-third border-2 border-league-rank-border-third';
        default:
          return 'bg-neutral-4 text-neutral-10';
      }
    },
    isCardDisplay() {
      return this.displayType === 'card';
    },
    changePercentBg() {
      return this.overallReturn < 0
        ? 'bg-change-percentage-negative'
        : 'bg-change-percentage-positive';
    },
  },
  methods: {
    getBorderColor() {
      if (!this.showRankingBorders) {
        return '';
      }
      if (this.rank < 0 || this.rank > 3) {
        return '';
      }
      const res = 'border-t-4.5 border-b-2 border-l-2 border-r-2';
      switch (this.rank) {
        case 1:
          return res + ' border-yellow-500';
        case 2:
          return res + ' border-gray-400';
        case 3:
          return res + ' border-yellow-700';
        default:
          return '';
      }
    },
    getBackgroundColor() {
      const background =
        this.isFavorited && this.isOnboarding
          ? ' favorited-card-background'
          : '';
      return background;
    },
    getFirstParentClasses() {
      const classes = `${this.isRepost ? 'w-min' : ''}`;

      return classes;
    },
    handleFavoritePortfolio() {
      eventManager.raisePortfolioInteractionEvent({
        event_category: 'Portfolio Interaction',
        event_action: PortfolioInteractionType.Favorite,
        portfolio_name: this.name,
      });
      this.$emit('onFavoritePortfolio');
    },
  },
};
